import { useState, useCallback, useRef } from "react";
import { apiClient } from "../../utils/api";
import { Agent, AgentSearchResponse } from "../../types/agent";

interface UseAgentSearchParams {
  category?: string;
  pageSize?: number;
}

interface UseAgentSearchReturn {
  searchResults: Agent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
  hasMore: boolean;
  searchAgents: (query: string) => Promise<void>;
  loadMore: () => Promise<void>;
  clearSearch: () => void;
  currentQuery: string;
}

const useAgentSearch = (params: UseAgentSearchParams = {}): UseAgentSearchReturn => {
  const { category = "all", pageSize = 15 } = params;
  
  const [searchResults, setSearchResults] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentQuery, setCurrentQuery] = useState("");
  
  const isLoadingRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const fetchSearchResults = useCallback(async (
    query: string, 
    page: number = 1, 
    isAppend: boolean = false
  ) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasMore(false);
      return;
    }

    if (page === 1) {
      setIsLoading(true);
      isLoadingRef.current = true;
    } else {
      setIsLoadingMore(true);
      isLoadingMoreRef.current = true;
    }
    setError("");

    try {
      const queryParams: Record<string, string | number> = {
        page: page,
        q: query.trim(),
      };

      if (category && category !== "all") {
        queryParams.ct = category;
      }

      const response = await apiClient.get("/api/v1/agent/search", { 
        params: queryParams 
      });
      
      const responseData: AgentSearchResponse = response.data;

      if (responseData.status) {
        const newAgents = responseData.data;
        
        if (isAppend) {
          setSearchResults(prev => [...prev, ...newAgents]);
        } else {
          setSearchResults(newAgents);
        }

        setHasMore(newAgents.length >= pageSize);
        setCurrentPage(page);
      } else {
        setError("검색 결과를 불러오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("에이전트 검색 오류:", err);
      setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
      isLoadingMoreRef.current = false;
    }
  }, [category, pageSize]);



  const searchAgents = useCallback(async (query: string) => {
    if (isLoadingRef.current || isLoadingMoreRef.current) {
      return;
    }
    
    // 검색 시작 시 상태 초기화 
    setCurrentQuery(query);
    setCurrentPage(1);
    setHasMore(true);
    setError("");
    await fetchSearchResults(query, 1, false);
  }, [fetchSearchResults]);

  const loadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore && !isLoading && currentQuery && !isLoadingRef.current && !isLoadingMoreRef.current) {
      await fetchSearchResults(currentQuery, currentPage + 1, true);
    }
  }, [hasMore, isLoadingMore, isLoading, currentPage, currentQuery, fetchSearchResults]);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setCurrentQuery("");
    setCurrentPage(1);
    setHasMore(true);
    setError("");
  }, []);

  return {
    searchResults,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    searchAgents,
    loadMore,
    clearSearch,
    currentQuery,
  };
};

export default useAgentSearch;
export type { UseAgentSearchParams, UseAgentSearchReturn }; 